import { ChevronDownIcon, ChevronUpIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Stack, StackItem, Text } from '@prophecy/ui-v3';
import { useState } from 'react';
import styled from 'styled-components';

import { CATEGORY_COLOR } from './constants';
import { CompactCardPadding, Dot, SurfaceCard } from './styled';
import { RateRow, SapUsageDashboardData } from './types';
import { UsageBar } from './UsageBar';
import { formatCredits } from './utils';

const RateColumn = styled(Stack)`
  flex: 1 1 0;
  min-width: 0;
`;

const RateValue = styled.div`
  flex: none;
  width: ${theme.sizes.x64};
`;

function RateList({ rows }: { rows: RateRow[] }) {
  return (
    <Stack gap={theme.spaces.x4}>
      {rows.map((row) => (
        <Stack key={row.label} direction='horizontal' gap={theme.spaces.x16}>
          <RateValue>
            <Text level='sm' tone={theme.textColor.secondary}>
              {row.value}
            </Text>
          </RateValue>
          <Text level='sm' tone={theme.textColor.tertiary}>
            {row.label}
          </Text>
        </Stack>
      ))}
    </Stack>
  );
}

function RateSection({
  color,
  title,
  unit,
  children
}: {
  color: string;
  title: string;
  unit?: string;
  children: React.ReactNode;
}) {
  return (
    <RateColumn gap={theme.spaces.x12}>
      <Stack direction='horizontal' gap={theme.spaces.x6} alignY='center'>
        <Dot $color={color} />
        <Text level='sm' weight={theme.fontWeight.semiBold}>
          {title}
        </Text>
        {unit && (
          <Text level='sm' tone={theme.textColor.tertiary}>
            {unit}
          </Text>
        )}
      </Stack>
      {children}
    </RateColumn>
  );
}

function LegendItem({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <Stack direction='horizontal' gap={theme.spaces.x6} alignY='center'>
      <Dot $color={color} />
      <Text level='sm' tone={theme.textColor.tertiary}>
        {label}
      </Text>
      <Text level='sm' weight={theme.fontWeight.medium}>
        {formatCredits(value)}
      </Text>
    </Stack>
  );
}

export function CuBreakdown({ data }: { data: SapUsageDashboardData }) {
  const [showRates, setShowRates] = useState(false);
  const { categories, creditsConsumed, dataRates, aiRateNote, infrastructureRates } = data;

  return (
    <Stack gap={theme.spaces.x16}>
      <SurfaceCard data-test-id='sap-usage-cu-breakdown'>
        <CompactCardPadding gap={theme.spaces.x16}>
          <Stack direction='horizontal' alignY='center'>
            <Text level='sm' weight={theme.fontWeight.medium}>
              How your {formatCredits(creditsConsumed)} CU breaks down
            </Text>
            <StackItem grow='1' />
            <Text level='sm' tone={theme.textColor.tertiary}>
              Total used{' '}
              <Text level='sm' weight={theme.fontWeight.semiBold} elementType='span'>
                {formatCredits(creditsConsumed)} CU
              </Text>
            </Text>
          </Stack>

          <UsageBar
            height={theme.sizes.x20}
            borderRadius={theme.radius.x4}
            segments={categories.map((metric) => ({ value: metric.credits, color: CATEGORY_COLOR[metric.key] }))}
          />

          <Stack direction='horizontal' alignY='center' gap={theme.spaces.x24}>
            {categories.map((metric) => (
              <LegendItem
                key={metric.key}
                color={CATEGORY_COLOR[metric.key]}
                label={metric.label}
                value={metric.credits}
              />
            ))}
            <StackItem grow='1' />
            <Text level='xs' tone={theme.textColor.tertiary}>
              Infrastructure is fixed by your plan — it won't change mid-year
            </Text>
          </Stack>
        </CompactCardPadding>
      </SurfaceCard>

      <Stack direction='horizontal'>
        <Button
          size='s'
          variant='link'
          icon={showRates ? <ChevronUpIcon type='default' size='s' /> : <ChevronDownIcon type='default' size='s' />}
          iconPlacement='left'
          onClick={() => setShowRates((current) => !current)}
          data-test-id='sap-usage-toggle-cu-rates'>
          {showRates ? 'Hide how CU is calculated' : 'Show how CU is calculated'}
        </Button>
      </Stack>

      {showRates && (
        <SurfaceCard data-test-id='sap-usage-cu-rates'>
          <CompactCardPadding direction='horizontal' gap={theme.spaces.x24} alignY='start'>
            <RateSection color={CATEGORY_COLOR.data} title='Data' unit='(cu/gb)'>
              <RateList rows={dataRates} />
            </RateSection>

            <RateSection color={CATEGORY_COLOR.ai} title='AI' unit='(Cu/MTok)'>
              <Text level='sm' tone={theme.textColor.secondary}>
                {aiRateNote}
              </Text>
            </RateSection>

            <RateSection color={CATEGORY_COLOR.infrastructure} title='Infrastructure'>
              <RateList rows={infrastructureRates} />
            </RateSection>
          </CompactCardPadding>
        </SurfaceCard>
      )}
    </Stack>
  );
}
